// SegmentedRecorder.js — the Multi Recorder page's record button when
// `multirecord_context_and_error_prevention` is on (web and extension only).
//
// Drives the same headless recorder the MR++ tray uses (agent/multiRecorder.js:
// sliding context window, /audio/addSegmented), with the props interface of
// api/Recorder.js so the page can swap one for the other. Native keeps the
// classic AudioRecorder, which already posts to the segmenter itself — a second
// WebView getUserMedia would fight the single iOS AVAudioSession.
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import { MultiRecorder, RMS_SPEECH } from 'agent/multiRecorder';
import MicDropStatus from '../../api/MicDropStatus';
import { MultiRecordContext } from 'agent/multiRecordContext';

const LOG = '[MultiRecorder] [Segmented]';

const getMicButtonLabel = (label) => {
  const clean = label.replace(/^Default - /, '').trim();
  if (/\b(built[- ]?in|internal|integrated|microphone array|macbook)\b/i.test(clean)) return 'Built-in Mic';
  return clean.replace(/\bmicrophone\b/gi, 'Mic');
};

const formatElapsed = (ms) => {
  const total = Math.floor(ms / 1000);
  const minutes = String(Math.floor(total / 60)).padStart(2, '0');
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const SegmentedRecorder = ({
  conversationIds,
  isDisabled,
  setStatus,
  refresh,
  onAttributed,
  onActiveChange,
  recordButtonId,
}) => {
  const { apiClient } = useContext(GlobalContext);
  const recorderRef = useRef(null);
  const controlsRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recording, setRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [wordChangeCount, setWordChangeCount] = useState(0);
  const speakingTimeoutRef = useRef(null);
  // After Stop, until the final chunk (or one parked offline) has uploaded.
  const [settling, setSettling] = useState(false);
  const [startedAt, setStartedAt] = useState(0);
  const [micDevices, setMicDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(() => {
    try {
      const saved = localStorage.getItem('comprehendpt_selectedMicId');
      return saved === 'default' ? '' : saved || '';
    }
    catch (_) { return ''; }
  });
  const [telehealth, setTelehealth] = useState(false);
  const active = recording || settling;

  useEffect(() => {
    if (!showDropdown) return undefined;
    const dismiss = (event) => {
      if (event.type === 'keydown' ? event.key === 'Escape' : !controlsRef.current?.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', dismiss);
    };
  }, [showDropdown]);

  const refreshMicDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices?.enumerateDevices?.();
      if (devices) setMicDevices(devices.filter((device) => device.kind === 'audioinput'));
    } catch (_) { /* Permission may not be available until Start. */ }
  }, []);

  useEffect(() => {
    refreshMicDevices();
    navigator.mediaDevices?.addEventListener?.('devicechange', refreshMicDevices);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refreshMicDevices);
  }, [refreshMicDevices]);

  useEffect(() => {
    if (deviceId && deviceId !== 'default' && micDevices.length &&
        !micDevices.some((device) => device.deviceId === deviceId)) {
      setDeviceId('');
      return;
    }
    try { localStorage.setItem('comprehendpt_selectedMicId', deviceId); }
    catch (_) { /* Recording still works without storage. */ }
  }, [deviceId, micDevices]);

  useEffect(() => {
    if (!active || !navigator.wakeLock) return undefined;
    let cancelled = false;
    let lock;
    navigator.wakeLock.request('screen').then((acquired) => {
      if (cancelled) acquired.release().catch(() => {});
      else lock = acquired;
    }).catch(() => {});
    return () => {
      cancelled = true;
      lock?.release().catch(() => {});
    };
  }, [active]);

  // Latest callbacks, read at call time: uploads outlive renders (and unmount).
  const callbacksRef = useRef({});
  callbacksRef.current = { setStatus, refresh, onAttributed };
  // Whether to ask for unplaced lines is fixed for the life of the recorder.
  const allowUnknownRef = useRef(typeof onAttributed === 'function');

  useEffect(() => {
    const recorder = new MultiRecorder({
      apiClient,
      // In memory only: one sitting on this page, and the window is patient text.
      context: new MultiRecordContext(),
      onMicLevel: (level) => {
        if (level >= RMS_SPEECH) {
          setWordChangeCount((count) => count + 1);
          clearTimeout(speakingTimeoutRef.current);
          speakingTimeoutRef.current = null;
          setIsSpeaking(true);
        } else if (!speakingTimeoutRef.current) {
          speakingTimeoutRef.current = setTimeout(() => {
            setIsSpeaking(false);
            speakingTimeoutRef.current = null;
          }, 5000);
        }
      },
      onUtterances: () => callbacksRef.current.refresh?.(),
      onAttributed: allowUnknownRef.current
        ? (...args) => callbacksRef.current.onAttributed?.(...args)
        : undefined,
      onError: (error) => {
        console.error(`${LOG} chunk failed:`, error);
        callbacksRef.current.setStatus?.(
          'A recording chunk failed to upload. Check your visits.',
        );
      },
    });
    recorderRef.current = recorder;
    return () => {
      // dispose() still flushes and uploads the chunk in progress.
      recorder.dispose();
      clearTimeout(speakingTimeoutRef.current);
      speakingTimeoutRef.current = null;
      recorderRef.current = null;
    };
  }, [apiClient]);

  useEffect(() => {
    if (!recording) {
      clearTimeout(speakingTimeoutRef.current);
      speakingTimeoutRef.current = null;
      setWordChangeCount(0);
      setIsSpeaking(false);
    }
  }, [recording]);

  useEffect(() => {
    onActiveChange?.(recording || settling);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, settling]);

  // The recorder has no "drained" event; poll its queue until it is empty.
  useEffect(() => {
    if (!settling) return undefined;
    const timer = setInterval(() => {
      const recorder = recorderRef.current;
      const busy =
        recorder &&
        (recorder.stopPending || recorder.draining || recorder.queue.length > 0);
      if (!busy) setSettling(false);
    }, 500);
    return () => clearInterval(timer);
  }, [settling]);

  useEffect(() => {
    if (!recording) return undefined;
    const tick = () =>
      callbacksRef.current.setStatus?.(
        `Recording: ${formatElapsed(Date.now() - startedAt)}`,
      );
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [recording, startedAt]);

  const stop = () => {
    recorderRef.current?.stop();
    setSettling(true);
    setRecording(false);
    callbacksRef.current.setStatus?.('Uploading Audio...');
  };

  // A selection change while recording cuts the chunk, like the classic
  // recorder does; the chunk in flight keeps the set it was spoken under.
  const targets = (conversationIds || []).map(Number).filter(Number.isFinite);
  const targetsKey = targets.join(',');
  useEffect(() => {
    if (!recording) return;
    if (!targets.length) {
      console.log(`${LOG} selection emptied; stopping`);
      stop();
      return;
    }
    recorderRef.current?.setTargets(targets);
    // Only a real selection change retargets; start() already takes the set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsKey]);

  const start = () => {
    const recorder = recorderRef.current;
    if (!recorder || !targets.length) return;
    if (!apiClient.actionAllowed()) {
      callbacksRef.current.setStatus?.('No Credits Remaining');
      return;
    }
    setShowDropdown(false);
    setStartedAt(Date.now());
    setRecording(true);
    recorder.start(targets, {
      deviceId,
      telehealth,
      onEnded: () => {
        setRecording(false);
        setSettling(true);
        callbacksRef.current.setStatus?.('Call audio sharing ended. Recording stopped; uploading captured audio.');
      },
    }).then(refreshMicDevices).catch((error) => {
      console.error(`${LOG} could not open the mic:`, error);
      setRecording(false);
      callbacksRef.current.setStatus?.(
        telehealth
          ? `Could not start Telehealth recording. ${error.message || 'Check microphone and audio-sharing permissions.'}`
          : 'Could not open the microphone. Check its permission and try again.',
      );
    });
  };

  const defaultMicLabel = micDevices.find((device) => device.deviceId === 'default')?.label || 'System default microphone';
  const activeMicLabel = (deviceId && micDevices.find((device) => device.deviceId === deviceId)?.label) || defaultMicLabel;
  const buttonMicLabel = getMicButtonLabel(activeMicLabel);

  return (
    <div ref={controlsRef} style={{ position: 'relative' }}>
      <div className="dropdown-container mic-split-group" style={{ display: 'flex', borderRadius: 4 }}>
        <Button
          id={recordButtonId}
          color="primary"
          size="md"
          disabled={!recording && (isDisabled || settling)}
          onClick={recording ? stop : start}
          title={recording ? 'Stop Recording' : 'Start Recording'}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: recording ? 'red' : 'white',
            borderRadius: '4px 0 0 4px', border: 'none', marginRight: 0 }}
        >
          {recording ? (
            <>
              <MicDropStatus isConnected={recording} wordChangeCount={wordChangeCount} fromRecorder={true} isSpeaking={isSpeaking} />
              <span style={{ marginLeft: '1px' }}>Stop</span>
            </>
          ) : (
            <>
              <span className="fa fa-microphone fa-2x" style={{ marginRight: 6, flexShrink: 0 }} />
              <span className="mic-label-stack" style={{ display: 'flex', flexDirection: 'column',
                alignItems: 'flex-start', minWidth: 0, width: 80, lineHeight: 1.05, textAlign: 'left' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Record</span>
                <span title={activeMicLabel} style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap', fontSize: 10, fontWeight: 400, marginTop: 1 }}>{buttonMicLabel}</span>
              </span>
            </>
          )}
        </Button>
        {!recording && (
          <Button
            color="primary"
            aria-label="Recording settings"
            aria-expanded={showDropdown}
            disabled={active || isDisabled}
            onClick={() => setShowDropdown(!showDropdown)}
            style={{ borderRadius: '0 4px 4px 0', padding: '0 8px', minWidth: 'auto', marginLeft: 0,
              border: 'none', borderLeft: '1px solid rgba(255,255,255,0.2)' }}
          >
            <span className="fa fa-chevron-down" />
          </Button>
        )}
      </div>
      {showDropdown && !active && (
        <div className="dropdown-container" style={{ position: 'absolute', top: '100%', left: 0,
          width: 330, maxWidth: 'calc(100vw - 32px)', backgroundColor: 'white', border: '1px solid #ccc',
          borderRadius: 4, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', zIndex: 1000, marginTop: 2 }}>
          <div style={{ padding: '8px 12px' }}>
            <label htmlFor={`${recordButtonId}-mic`} style={{ display: 'block', textAlign: 'center',
              fontSize: 11, color: '#666', fontWeight: 'bold', marginBottom: 4 }}>Select Microphone</label>
            <select
              id={`${recordButtonId}-mic`}
              value={deviceId}
              onChange={(event) => setDeviceId(event.target.value)}
              onFocus={refreshMicDevices}
              disabled={active || isDisabled}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14 }}
            >
              <option value="">{defaultMicLabel.replace(/^Default - /, '')}</option>
              {micDevices.filter((device) => device.deviceId && device.deviceId !== 'default').map((device, index) => (
                <option key={device.deviceId} value={device.deviceId}>{device.label || `Microphone ${index + 1}`}</option>
              ))}
            </select>
          </div>
          {[{ value: false, icon: '🏥', label: 'In-clinic', description: 'Records your voice (patient speaks directly to you)' },
            { value: true, icon: '💻', label: 'Telehealth', description: 'Records both you and patient from telehealth visit' }].map((mode) => (
            <button
              key={mode.label}
              type="button"
              aria-pressed={telehealth === mode.value}
              onClick={() => { setTelehealth(mode.value); setShowDropdown(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
                border: 0, borderTop: '1px solid #eee', textAlign: 'left', color: 'inherit', cursor: 'pointer',
                backgroundColor: telehealth === mode.value ? '#f0f8ff' : 'white', fontSize: 14 }}
            >
              <span>{mode.icon}</span>
              <span><span style={{ display: 'block', fontWeight: 'bold' }}>{mode.label}</span>
                <span style={{ fontSize: 11, color: '#666' }}>{mode.description}</span></span>
              {telehealth === mode.value && <span className="fa fa-check" style={{ marginLeft: 'auto', color: '#007bff' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SegmentedRecorder;
